import React, { useEffect, useState } from 'react';
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { createPost, uploadPostPicture } from '../../redux/post/postSlice';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getAddressSuggestions } from '../../utils/locationUtils';
import { useNotification } from '../shared/NotificationProvider';

import PostBasicInfo from './components/PostBasicInfo';
import PostLocationInfo from './components/PostLocationInfo';
import RoommateInfo from './components/RoommateInfo';
import BehaviorInputs from './components/BehaviorInputs';
import ImageUploadSection from './components/ImageUploadSection';
import AmenitiesSection from './components/AmenitiesSection';
import FormActions from './components/FormActions';

const CreateAndEditPostForm = ({ myPost }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const postImages = useSelector(state => state.post.myPost.postImages);
  const notify = useNotification();

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
    setValue
  } = useForm({
    defaultValues: {
      title: myPost?.title || '',
      description: myPost?.description || '',
      location: myPost?.location || '',
      rent: myPost?.rent || '',
      availableFrom: new Date(myPost?.availableFrom) || new Date(),
      currentRoommates: myPost?.currentRoommates || '',
      neededRoommates: myPost?.neededRoommates || '',
      contactInfo: myPost?.contactInfo || '',
      roommateShouldHaveGender: myPost?.roommateShouldHaveGender || '',
      expectedBehaviours: myPost?.expectedBehaviours || [],
      unacceptableBehaviours: myPost?.unacceptableBehaviours || [],
      highlights: myPost?.highlights || [],
      amenities: myPost?.amenities || [],
    }
  });

  useEffect(() => {
    if (myPost) {
      reset(myPost);
    }
  }, [myPost, reset]);

  const handleUploadFile = async (e, files, setOpen) => {
    setOpen(false);
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('homeImages', file);
      });

      const res = await dispatch(uploadPostPicture(formData));
      notify[res.payload.status === 'success' ? 'success' : 'error'](res.payload.message);
    } catch {
      notify.error("Some system level error occurred");
    }
  };

  const fetchAddressSuggestions = async (value) => {
    if (value.trim().length >= 3) {
      try {
        setIsLoadingSuggestions(true);
        const results = await getAddressSuggestions(value);
        setAddressSuggestions(results);
      } catch {
        setAddressSuggestions([]);
      } finally {
        setIsLoadingSuggestions(false);
      }
    } else {
      setAddressSuggestions([]);
    }
  };

  const debouncedFetchSuggestions = debounce(fetchAddressSuggestions, 300);

  const onSubmit = async (data) => {
    try {
      const res = await dispatch(createPost(data));
      notify[res.payload.status === 'success' ? 'success' : 'error'](res.payload.message);
    } catch {
      notify.error("Some system level error occurred");
    }
  };

  const resetForm = () => {
    reset();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={isSmallScreen ? 1.5 : 3}>
          <PostBasicInfo register={register} errors={errors} control={control} />

          <PostLocationInfo
            control={control}
            errors={errors}
            addressSuggestions={addressSuggestions}
            setAddressSuggestions={setAddressSuggestions}
            isLoadingSuggestions={isLoadingSuggestions}
            debouncedFetchSuggestions={debouncedFetchSuggestions}
            setValue={setValue}
            availableFrom={myPost?.availableFrom ? new Date(myPost.availableFrom) : null}
          />

          <RoommateInfo register={register} control={control} />

          <ImageUploadSection
            postImages={postImages}
            handleUploadFile={handleUploadFile}
            dispatch={dispatch}
          />

          <AmenitiesSection control={control} />

          <BehaviorInputs control={control} />

          <FormActions resetForm={resetForm} />
        </Grid>
      </form>
    </LocalizationProvider>
  );
};

// Helper functions
function debounce(func, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
}

export default CreateAndEditPostForm;
